import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { PageArrivee } from '../pages/PageArrivee';
import { PageCours } from '../pages/PageCours';
import { PageAnnuaire } from '../pages/PageAnnuaire';
import { PageEspace } from '../pages/PageEspace';
import { PageHistorique } from '../pages/PageHistorique';
import { PageObservation } from '../pages/PageObservation';
import { PageReglages } from '../pages/PageReglages';
import { PageRelecture } from '../pages/PageRelecture';
import { PageCatalogue } from '../design/PageCatalogue';
import { CadreApplication } from './CadreApplication';
import { PortailDeSession } from './PortailDeSession';

/**
 * **Les routes, DÉCLARÉES EN CONFIGURATION.**
 *
 * `DOCTRINE.md` §6 et `web/CLAUDE.md` : « React Router 7 **en mode déclaratif
 * uniquement**. Le mode framework de React Router est un métaframework déguisé, avec
 * routage par fichiers, chargeurs de données et rendu serveur : **il est interdit.** »
 * Il n'y a donc ici ni `loader`, ni `action`, ni fichier de route : les données passent
 * par TanStack Query, dans des hooks nommés.
 *
 * **VERSION 7 ET NON 8**, alors que la 8 existe : la stack est figée par `CLAUDE.md` et
 * par `DOCTRINE.md` §6, et « tout changement se décide explicitement, se répercute là, et
 * incrémente la doctrine ». Monter de majeure au passage aurait été un changement de
 * stack décidé par personne.
 *
 * **IL N'Y A PAS DE ROUTE DE CONNEXION EN PRODUCTION, ET C'EST UNE RÈGLE DU CORPUS.**
 * `CDC-6` F-71 : « **l'écran de connexion est une page du site vitrine** », page 6 de la
 * liste fermée des huit. `web/` n'en porte donc aucune : `PortailDeSession` renvoie vers
 * `SITE_URL`. Voir son fichier pour ce que cela coûte, et pour l'entrée de développement.
 */
export function Routeur() {
  return (
    <BrowserRouter>
      <Routes>
        <Route element={<PortailDeSession />}>
          {/* **Les écrans persistants partagent le cadre** : une seule barre, déclarée
              une seule fois, que nulle page n'a à se rappeler de porter. */}
          <Route element={<CadreApplication />}>
            <Route path="/" element={<Navigate to="/arrivee" replace />} />
            <Route path="/arrivee" element={<PageArrivee />} />
            <Route path="/historique" element={<PageHistorique />} />
            {/* **Une seule surface, et le fil s'ouvre DEDANS** — F-50 : « la bulle du
                chat est présente partout, et cette surface est ce qu'elle ouvre en
                grand ». Le fil est donc un paramètre de la même page, pas un écran à
                part : l'ouvrir ailleurs ferait perdre la liste depuis laquelle on
                circule. */}
            <Route path="/annuaire" element={<PageAnnuaire />} />
            <Route
              path="/annuaire/:conversationId"
              element={<PageAnnuaire />}
            />
            <Route path="/espace" element={<PageEspace />} />
            <Route path="/reglages" element={<PageReglages />} />
          </Route>
          {/* **Le cours entier tient dans un seul écran** : « ils n'y naviguent pas,
              ils y travaillent ». Il n'y a donc aucune sous-route sous le tableau, et
              **pas de cadre non plus** — la barre prendrait de la place sur le tracé. */}
          <Route path="/cours/:coursId" element={<PageCours />} />
          {/* **La relecture s'ouvre dans la MÊME surface que le tableau d'un cours
              vivant** — F-47 : « et non dans une fenêtre à part, deux surfaces feraient
              deux fois le même écran ». Elle est donc hors du cadre, comme lui. */}
          <Route path="/relecture/:coursId" element={<PageRelecture />} />
          {/* ═══════════════════════════════════════════════════════════════════
              **F-20 — L'OBSERVATION D'UN COURS EN DIRECT, ET ELLE EST ICI PARCE
              QUE LE MOTEUR DE TABLEAU L'EST.**
              ═══════════════════════════════════════════════════════════════════

              **Le geste part de la surface d'administration**, qui « y renvoie par URL »,
              et la borne de Q567 est portée par la route qu'elle appelle — « le geste n'a
              plus qu'un chemin » (Q566). Ce que cette page ajoute est ce que F-20 promet
              et que deux compteurs ne pouvaient pas rendre : **le tableau qui se remplit
              et les curseurs nommés**.

              **Pourquoi elle n'est pas dans `admin/`** : le moteur de tableau vit dans
              `features/cours/moteur.tsx`, **seul fichier du dépôt autorisé à le
              connaître** (D-012), et `web/`, `site/` et `admin/` « ne se parlent jamais
              entre eux : aucun import direct, aucun composant partagé ». Un renvoi par
              URL n'est pas un import. Les deux autres issues sont fermées, et le motif
              complet vit dans `features/cours/observation.ts`.

              **Hors du cadre, comme le cours et la relecture** : c'est la même surface,
              et une barre de navigation y prendrait de la place sur le tracé. */}
          <Route path="/observation/:coursId" element={<PageObservation />} />
        </Route>
        {/* La page catalogue de `design-system-first` : « tout composant créé s'inscrit
            immédiatement dans l'inventaire de `DESIGN.md` **et dans la page catalogue
            `/design`** ». Elle ne demande aucune session — elle ne montre aucune donnée. */}
        <Route path="/design" element={<PageCatalogue />} />
        <Route path="*" element={<Navigate to="/arrivee" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
